import {
  categoryMerchandising,
  KlevuConfig,
  KlevuFetch,
  recentlyViewed,
  search,
  sendMerchandisingViewEvent,
  suggestions,
  trendingProducts,
// eslint-disable-next-line import/no-unresolved
} from '@klevu/core'

/**
 * Initialize Klevu
 * @param {String} url - Klevu store url
 * @param {String} key - Klevu api key
 */
export async function initKlevu(url, key) {
  const defaultSetup = KlevuConfig.default

  if (!url || !key || defaultSetup) {
    return
  }

  await KlevuConfig.init({
    url: `https://${url.toLowerCase()}.ksearchnet.com/cs/v2/search`,
    apiKey: key.toLowerCase(),
  })
}

/**
 * Fetch collection from Klevu.
 * @param {String} query - collection title.
 * @param {Object} options - Additional query options
 * @returns {Object}
 *
 */
export async function fetchCollection(query, options) {
  const result = await KlevuFetch(categoryMerchandising(
    query,
    options,
    sendMerchandisingViewEvent(query),
  ))

  const queries = result.queriesById('search')

  return queries ? queries : false
}

/**
 * Fetch search from Klevu.
 * @param {String} query - Search query.
 * @param {Object} options - Additional query options.
 * @returns {Object}
 */
export async function fetchSearchQuery(query, options) {
  const fetchedResults = []
  const result = await KlevuFetch(
    search(
      query,
      ...options,
    ),
    suggestions(
      query,
      {
        id: 'suggestion',
        limit: 4,
      },
    ),
  )

  const finalSuggestions = result.suggestionsById('suggestion')

  fetchedResults.result = { ...result.queriesById('search') }
  fetchedResults.suggestion = [...finalSuggestions.suggestions]

  return fetchedResults ? fetchedResults : false
}

/**
 * Fetch recently viewed products.
 * @param {Object} options - Additional query options.
 * @returns {Object}
 */
export async function fetchRecentlyViewed(options) {
  const result = await KlevuFetch(recentlyViewed(
    options,
  ))

  const queries = result.queriesById('recent')
  return queries ? queries : false
}

/**
 * Fetch trending products.
 * @param {Object} options - Additional query options.
 * @returns {Object}
 */
export async function fetchTrendingProducts(options) {
  const result = await KlevuFetch(trendingProducts(
    options,
  ))

  const queries = result.queriesById('trending')
  return queries ? queries : false
}

/**
 * Fetch suggestions based on search.
 * @param {String} query - Search query.
 * @param {Object} options - Additional query options.
 * @returns {Object}
 */
export async function fetchSuggestions(query, options) {
  const result = await KlevuFetch(
    suggestions(
      query,
      options,
    ),
  )

  const queries = result.suggestionsById('suggestion')
  return queries ? queries : false
}

/**
 * Updates product object to match Shopify.
 * @param {Array} collection - Results array.
 * @returns {Array}
 */
export function sanitizedCollectionObject(collection) {
  const sanitizedCollection = [...collection].map((product) => {
    const placeholderImage = cnvs.urls.files.replace('placeholder', `placeholder-image_x400.png`)
    const imageSrc = product.imageUrl === '' ? placeholderImage : product.imageUrl
    const images = [{ src: imageSrc }]
    const tags = product.tags ? product.tags.split(',') : null
    const badge = getProductBadges(tags)
    const price = product.salePrice || product.price
    const startPrice = product.startPrice
    const compareAtPrice = product.price
    let compatibility = false

    if (product.imageHover && product.imageHover !== '') {
      images.push({ src: product.imageHover })
    }

    if (product.compatible_with || product.compatible_with_new) {
      const model = product.compatible_with || product.compatible_with_new
      const modelString = model.replace(/[[(,)\]']+/g, '')

      compatibility = modelString.includes('|')
        ? modelString
        : modelString.split(' ').join('|')
    }

    return {
      available: product.inStock === 'yes',
      badge,
      compareAtPrice: { amount: Number(compareAtPrice) * 100 },
      compatibility,
      featuredImage: {
        src: imageSrc,
      },
      handle: product.url.split('/').pop(),
      hasOnlyDefaultVariant: product.totalVariants <= 0,
      secondaryImage: {
        src: product.imageHover === '' ? false : product.imageHover,
      },
      images,
      id: product.id,
      price: {
        amount: Number(price) * 100,
        startPrice: startPrice > 0 ? Number(startPrice) * 100 : false,
      },
      sku: product.sku,
      title: product.name,
      url: product.url,
    }
  })

  return sanitizedCollection
}

function getProductBadges(tags) {
  if (!tags) {
    return false
  }

  const badges = tags.filter((tag) => tag.includes('badge '))
  const badgeTitle = badges.map((badge) => badge.replace('badge ', ''))

  return badgeTitle
}
